import React, { type ReactNode, useEffect } from 'react';
import { AnimatedCollapse } from '@components/animatedCollapse';
import { useFlexAnimation } from '../context/FlexAnimationContext';

interface FlexAnimatedCollapseProps {
  children: ReactNode;
  shouldShow: boolean;
  position: 'left' | 'right';
  width: string;
}

export const FlexAnimatedCollapse: React.FC<FlexAnimatedCollapseProps> = ({
  children,
  shouldShow,
  position,
  width,
}) => {
  const {
    leftVisible,
    rightVisible,
    setLeftVisible,
    setRightVisible,
    leftWidth,
    rightWidth,
    setLeftWidth,
    setRightWidth,
  } = useFlexAnimation();

  // Update visibility state when shouldShow changes
  useEffect(() => {
    if (position === 'left') {
      setLeftVisible(shouldShow);
      setLeftWidth(width);
    } else {
      setRightVisible(shouldShow);
      setRightWidth(width);
    }
  }, [
    shouldShow,
    position,
    setLeftVisible,
    setRightVisible,
    width,
    setLeftWidth,
    setRightWidth,
  ]);

  // Calculate flex basis based on what's visible
  const getFlexBasis = (): string => {
    if (leftVisible && rightVisible) {
      return position === 'left' ? leftWidth : rightWidth;
    } else if (leftVisible || rightVisible) {
      return '100%'; // Only one visible: take full width
    } else {
      return '0%'; // Neither visible: collapse
    }
  };

  return (
    <AnimatedCollapse
      shouldShow={shouldShow}
      preserveFlex={true}
      flexBasisExpanded={getFlexBasis()}
    >
      {children}
    </AnimatedCollapse>
  );
};
